import { useEffect } from "react";

declare global {
    interface Window {
        google?: any;
    }
}

export type GoogleAddressResult = {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    formatted: string;
};

type Options = {
    country?: string;
};

export function useGoogleAddressAutocomplete(
    inputRef: React.RefObject<HTMLInputElement | null>,
    onSelect: (address: GoogleAddressResult) => void,
    options: Options = {}
) {
    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

        if (!apiKey) {
            console.warn("Missing REACT_APP_GOOGLE_MAPS_API_KEY");
            return;
        }

        let autocomplete: any = null;
        let listener: any = null;
        let cancelled = false;

        let initAttempts = 0;

        const initAutocomplete = () => {
            if (cancelled) return;

            const input = inputRef.current;

            if (!window.google?.maps?.places || !input) {
                initAttempts += 1;

                if (initAttempts < 20) {
                    window.setTimeout(initAutocomplete, 100);
                }

                return;
            }

            autocomplete = new window.google.maps.places.Autocomplete(input, {
                componentRestrictions: { country: options.country || "ca" },
                fields: ["address_components", "formatted_address"],
                types: ["address"],
            });

            listener = autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                const components = place.address_components || [];

                const getPart = (type: string) =>
                    components.find((c: any) => c.types.includes(type))?.long_name || "";

                const streetNumber = getPart("street_number");
                const route = getPart("route");

                const city =
                    getPart("locality") ||
                    getPart("postal_town") ||
                    getPart("administrative_area_level_3");

                onSelect({
                    street: [streetNumber, route].filter(Boolean).join(" "),
                    city,
                    state: getPart("administrative_area_level_1"),
                    postalCode: getPart("postal_code"),
                    country: getPart("country") || "CA",
                    formatted: place.formatted_address || "",
                });
            });
        };

        const loadScript = () => {
            const existingScript = document.querySelector<HTMLScriptElement>(
                'script[data-google-places="true"]'
            );

            if (existingScript) {
                if (window.google?.maps?.places) {
                    setTimeout(initAutocomplete, 0);
                } else {
                    existingScript.addEventListener("load", initAutocomplete, { once: true });
                }

                return;
            }

            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.dataset.googlePlaces = "true";
            script.addEventListener("load", initAutocomplete, { once: true });
            document.head.appendChild(script);
        };

        const t = window.setTimeout(loadScript, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(t);

            if (listener?.remove) {
                listener.remove();
            }

            autocomplete = null;
        };
    }, [inputRef, onSelect, options.country]);
}